import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';

import { environment } from '../../environments/environment';

export interface LanguageField {
  nameEn?: string;
  nameTc?: string;
  nameSc?: string;
  descEn?: string;
  descTc?: string;
  descSc?: string;
  remarkEn?: string;
  remarkTc?: string;
  remarkSc?: string;
  titleEn?: string;
  titleTc?: string;
  titleSc?: string;
  contentEn?: string;
  contentTc?: string;
  contentSc?: string;
  coachRemarkEn?: string;
  coachRemarkTc?: string;
  coachRemarkSc?: string;
  overrideTimeDescEn?: string;
  overrideTimeDescTc?: string;
  overrideTimeDescSc?: string;
  tncEn?: string;
  tncTc?: string;
  tncSc?: string;
  facilityRemarkEn?: string;
  facilityRemarkTc?: string;
  facilityRemarkSc?: string;
  redeemPromotionTncEn?: string;
  redeemPromotionTncTc?: string;
  redeemPromotionTncSc?: string;
}

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  private readonly transloco = inject(TranslocoService);
  private readonly router = inject(Router);

  activeLang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });

  number2Arr(num: number): any[] {
    return Array.from({ length: num }, (_, i) => i + 1);
  }

  getDaysInMonth(month: number): number {
    // month: 1 (January) to 12 (December)
    const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return daysInMonth[month - 1] || 0;
  }

  orderNoDisplay(value?: number | null): string {
    return value ? `${value}`.padStart(10, '0') : '';
  }

  routeToHome() {
    this.navigate('home');
  }
  routeToNews() {
    this.navigate('news');
  }
  routeToNewsDetails(newsId: number) {
    this.navigate(`news/details/${newsId}`);
  }
  routeToTnc() {
    this.navigate(`tnc`);
  }
  routeToFacilityLocation() {
    this.navigate('facility/location');
  }
  routeToFacilityBooking(locationId: number) {
    this.navigate(`facility/booking/${locationId}`);
  }
  routeToAccessPassList() {
    this.navigate('access-pass/list');
  }
  routeToCreditPackageList() {
    this.navigate('credit-package/list');
  }
  routeToLogin() {
    this.navigate('login');
  }
  routeToForgetPassword() {
    this.navigate('forget-password');
  }
  routeToRegister() {
    this.navigate('register');
  }
  routeToMember() {
    this.navigate('member');
  }
  routeToProfile() {
    this.navigate('member/profile');
  }
  routeToMyorderList() {
    this.navigate('member/myorder-list');
  }
  routeToMyorderDetails(myorderId?: number) {
    this.navigate(`member/myorder-details/${myorderId}`);
  }
  routeToCart() {
    this.navigate('cart');
  }

  private navigate(path: string): void {
    this.router.navigate([`/${this.activeLang()}/${path}`]);
  }

  goToPaymentGatewayEndpoint(myorderId: number, paymentResultStatus: string): void {
    const isApp: string = '0';
    const url = `${environment.paymentGatewayEndpoint}${myorderId}/${isApp}/${paymentResultStatus}`;
    setTimeout(() => {
      window.location.href = url;
    }, 300);
  }

  scrollToFirstInvalidControl(formGroup: FormGroup): void {
    const invalidKey = Object.keys(formGroup.controls).find((key) => formGroup.get(key)?.invalid);
    if (!invalidKey) return;

    const el = Array.from(document.querySelectorAll<HTMLElement>(`[formControlName="${invalidKey}"]`)).find((el) => el.offsetParent !== null);

    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // For fieldByLanguage Pipe, no need to use this function directly
  private get langSuffix(): 'En' | 'Tc' | 'Sc' {
    const lang = this.activeLang();
    if (lang === 'tc') return 'Tc';
    if (lang === 'sc') return 'Sc';
    return 'En';
  }

  fieldByLanguage<T extends LanguageField>(
    obj?: T | null,
    fieldName?: 'name' | 'desc' | 'remark' | 'title' | 'content' | 'coachRemark' | 'overrideTimeDesc' | 'tnc' | 'facilityRemark' | 'redeemPromotionTnc',
  ): string {
    if (!obj || !fieldName) return '';
    const key = `${fieldName}${this.langSuffix}` as keyof LanguageField;
    return (obj[key] as string | undefined) ?? '';
  }

  fieldByLanguageChineseOnly<T extends LanguageField>(obj?: T | null, fieldName?: 'name') {
    if (!obj || !fieldName) return '';
    const key = `${fieldName}${this.activeLang() === 'sc' ? 'Sc' : 'Tc'}` as keyof LanguageField;
    return (obj[key] as string | undefined) ?? '';
  }
}
